// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  AttendeesInfoFormValidator,
  GuestWaitingList,
  SurveyExtensionPoint,
} from '@seaters/constants';
import {
  useCheckoutStore,
  useFetchGuestSurvey,
  useUpdateGuestAttendeeInfo,
} from '@seaters/data-access';
import { AttendeeInfoStep, SurveyForm } from '@seaters/ui-shared';
import { Modal, Spin, Steps } from 'antd';
import { useFormSteps } from './hooks/useFormSteps';
import { useTranslation } from 'react-i18next';

type CheckoutFormProps = {
  waitingListData: GuestWaitingList;
  handleAcceptAsGuest: () => void;
  isLoading: boolean;
};

const contentStyle: React.CSSProperties = {
  marginTop: 32,
};

export function CheckoutForm({
  waitingListData,
  handleAcceptAsGuest,
  isLoading,
}: CheckoutFormProps) {
  const { t } = useTranslation();
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code') ?? '';
  const waitingListId = searchParams.get('waitingListId') ?? '';

  const { data: surveyInstances } = useFetchGuestSurvey({
    code,
    waitinglist_id: waitingListId,
    extension_point: SurveyExtensionPoint.AT_CHECKOUT,
  });

  const { isCheckoutShown, setCheckoutShown } = useCheckoutStore();

  const { mutate: updateGuestAttendeesInfo } = useUpdateGuestAttendeeInfo(
    waitingListId,
    { code }
  );

  const surveyStep = {
    key: 'SURVEY_FORM',
    title: t('survey_title_text'),
    content: () => {
      return (
        <SurveyForm
          ticketsAmount={waitingListData?.position.numberOfSeats}
          surveyInstances={surveyInstances}
          code={code}
          next={nextStep}
          prev={prevStep}
          isLastStep={isLastStep}
        />
      );
    },
    submitText: 'finish_button_text',
    orderIndex: 0,
  };

  const attendeeInfoStep = {
    key: 'ATTENDEE_INFO',
    title: t('checkout_requiredinfo_steptitle'),
    content: () => {
      return (
        <AttendeeInfoStep
          handleAttendeeForm={handleAttendeeForm}
          next={nextStep}
          prev={prevStep}
          waitingList={waitingListData}
        />
      );
    },
    submitText: 'guest_app_confirm',
    orderIndex: 1,
  };

  const visibility = {
    [surveyStep.key]: !!surveyInstances?.content.length,
    [attendeeInfoStep.key]: waitingListData?.eventRequiredAttendeeInfo?.length,
  };

  const steps = [surveyStep, attendeeInfoStep];

  const filteredSteps = steps.filter((step) => visibility[step.key]);

  const { nextStep, prevStep, currentStep, isLastStep } = useFormSteps(
    filteredSteps.length,
    handleAcceptAsGuest
  );

  const handleAttendeeForm = (attendeeValues: AttendeesInfoFormValidator[]) => {
    updateGuestAttendeesInfo(
      { attendeeInfo: { attendees: attendeeValues } },
      {
        onSuccess: () => {
          nextStep();
          // closeModal();
        },
      }
    );
  };

  const StepComponent = filteredSteps[currentStep]
    ? filteredSteps[currentStep].content()
    : null;

  return (
    <Modal
      open={isCheckoutShown}
      style={{ maxWidth: 1284 }}
      title={t('checkout_title')}
      onCancel={() => setCheckoutShown(false)}
      footer={false}
      width={'100%'}
      maskClosable
    >
      <Spin spinning={isLoading}>
        {filteredSteps.length > 1 ? (
          <Steps
            current={currentStep}
            items={filteredSteps
              .sort((a, b) => a.orderIndex - b.orderIndex)
              .map(({ key, title }) => ({ key, title }))}
          />
        ) : null}
        <div style={contentStyle} data-testid="checkoutModalContent">
          {StepComponent}
        </div>
      </Spin>
    </Modal>
  );
}
